import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { OwnerOnly } from '@/lib/auth/OwnerOnly';
import { EdgeFunctionError } from '@/lib/api/edgeFunction';
import {
  isLegacyPaymentModel,
  SETTLEMENT_MODES,
  useBusinessListings,
  useBusinessSettlementInfo,
  useCreatePayRail,
  useListingSettlement,
  usePayRails,
  useWriteListingSettlement,
  type PayRailKind,
  type PayRailProvider,
  type SettlementMode,
} from '@/lib/data/settlement';
import { RefundPolicyCard } from '@/lib/settlement/RefundPolicyCard';
import { REFUND_POLICY_LABEL, resolveRefundPolicy } from '@namibiago/shared/policy';
import { AlertIcon } from '@/icons';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/settlement/')({
  component: SettlementPage,
});

const PROVIDER_OPTIONS: { value: PayRailProvider; label: string }[] = [
  { value: 'dpo', label: 'DPO Group' },
  { value: 'finatic', label: 'Finatic' },
  { value: 'paytoday', label: 'PayToday' },
  { value: 'fnb', label: 'FNB Namibia' },
  { value: 'standard_bank', label: 'Standard Bank Namibia' },
  { value: 'bank_windhoek', label: 'Bank Windhoek' },
  { value: 'nedbank', label: 'Nedbank Namibia' },
];

const BANK_PROVIDERS: PayRailProvider[] = ['fnb', 'standard_bank', 'bank_windhoek', 'nedbank'];

function SettlementPage() {
  return (
    <OwnerOnly>
      <SettlementContent />
    </OwnerOnly>
  );
}

function SettlementContent() {
  const { supabase } = useAuth();
  const { businessId } = useProperty();

  const listingsQuery = useBusinessListings(supabase, businessId);
  const bizQuery = useBusinessSettlementInfo(supabase, businessId);
  const railsQuery = usePayRails(supabase, businessId);

  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const listings = listingsQuery.data ?? [];
  const activeListingId = selectedListingId ?? listings[0]?.id ?? null;

  const [showAddRail, setShowAddRail] = useState(false);

  if (!businessId) {
    return <p className="text-body text-ink-2">Select a property to manage settlement settings.</p>;
  }

  const legacy = isLegacyPaymentModel(bizQuery.data?.payment_model ?? null);

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Settlement settings</h1>
        <p className="mt-sm text-body text-ink-2">
          Choose how each listing collects the balance guests owe beyond NamibiaGo's own fee. NamibiaGo never touches
          this money — it goes straight to your own pay link or bank details.
        </p>
      </div>

      {legacy ? (
        <div className="flex items-center gap-sm rounded-card border border-warning bg-tint-warning px-md py-sm text-body text-ink">
          <AlertIcon className="h-4 w-4 flex-shrink-0 text-warning" aria-hidden="true" />
          <span>
            Legacy — frozen. Your account's payment model (<strong>{bizQuery.data?.payment_model}</strong>) predates
            per-listing settlement and cannot be changed here. Contact support to move to the current model.
          </span>
        </div>
      ) : null}

      {/* Phase 22: restaurants carry no in-app money and no refund preset (Ian 10-24). */}
      {bizQuery.data && bizQuery.data.category !== 'restaurant' ? <RefundPolicyCard businessId={businessId} /> : null}

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Pay rails</h2>
        <p className="mt-xs text-body text-ink-2">
          A pay rail is your own DPO / Finatic / PayToday / bank link that collects the balance leg. New rails need an
          email code, an authenticator step-up, and a 24-hour cooling-off before they can be used.
        </p>

        {railsQuery.isLoading ? <PageLoading /> : null}
        {railsQuery.data && railsQuery.data.length > 0 ? (
          <ul className="mt-md flex flex-col gap-sm">
            {railsQuery.data.map((rail) => (
              <li
                key={rail.id}
                className="flex flex-wrap items-center justify-between gap-sm rounded-input border border-hair px-md py-sm"
              >
                <span className="text-body text-ink">
                  {PROVIDER_OPTIONS.find((p) => p.value === rail.provider)?.label ?? rail.provider} ·{' '}
                  <span className="text-ink-2">{rail.status.replace('_', ' ')}</span>
                </span>
                {rail.status === 'pending_verification' || rail.status === 'cooling_off' ? (
                  <Link
                    to="/settlement/verify"
                    search={{ railId: rail.id }}
                    className="text-caption font-semibold text-canyon underline"
                  >
                    Continue verification
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-md text-body text-ink-2">No pay rails linked yet.</p>
        )}

        <button
          type="button"
          onClick={() => setShowAddRail((v) => !v)}
          className="mt-md rounded-button border border-border bg-surface px-md py-xs text-body font-semibold text-ink"
        >
          {showAddRail ? 'Cancel' : 'Add a pay rail'}
        </button>

        {showAddRail ? <AddRailForm businessId={businessId} onDone={() => setShowAddRail(false)} /> : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Per-listing settlement</h2>
        {listingsQuery.isLoading ? <PageLoading label="Loading listings…" compact /> : null}
        {listings.length === 0 && !listingsQuery.isLoading ? (
          <p className="mt-sm text-body text-ink-2">Add a listing first.</p>
        ) : null}
        {listings.length > 0 ? (
          <div className="mt-md flex flex-col gap-md">
            <label className="flex flex-col gap-xs text-caption text-ink-2">
              Listing
              <select
                value={activeListingId ?? ''}
                onChange={(e) => setSelectedListingId(e.target.value)}
                className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
              >
                {listings.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))}
              </select>
            </label>
            {activeListingId ? (
              // key={activeListingId}: fully remounts the form on a listing
              // switch (fix round 1, Important #3) — the field-hydration
              // effect below re-runs on any `row` change, including this
              // form's own save-triggered refetch (needed for BUG-1's
              // "Saved." fix), so re-adding `listingId` to THAT effect's
              // deps risked the same footgun again the moment it changed
              // for reasons unrelated to a real listing switch. A remount
              // is unconditionally correct: every piece of local state
              // (mode/depositPct/railId/refundTerms/saved/error) starts
              // fresh for the new listing, full stop — two listings that
              // both have no settlement row (or identical values) can no
              // longer leave one's unsaved edits sitting in the other's
              // form.
              <ListingSettlementForm
                key={activeListingId}
                listingId={activeListingId}
                businessFallbackPct={bizQuery.data?.host_deposit_pct ?? null}
                businessFallbackTerms={bizQuery.data?.host_cancellation_terms ?? null}
                fullPrepayUnlocked={!!bizQuery.data?.full_prepay_unlocked}
                policyLabel={REFUND_POLICY_LABEL[resolveRefundPolicy(bizQuery.data?.refund_policy)]}
                legacy={legacy}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function AddRailForm({ businessId, onDone }: { businessId: string; onDone: () => void }) {
  const { supabase } = useAuth();
  const createRail = useCreatePayRail(supabase);
  const [provider, setProvider] = useState<PayRailProvider>('dpo');
  const [kind, setKind] = useState<PayRailKind>('static_link');
  const [url, setUrl] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isBank = kind === 'bank_details';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    createRail.mutate(
      {
        businessId,
        provider,
        kind,
        ...(isBank
          ? { accountHolder, accountNumber, branchCode }
          : { url }),
      },
      {
        onSuccess: onDone,
        onError: (err) => {
          if (err instanceof EdgeFunctionError && (err.code === 'aal2_required' || err.code === 'mfa_enrollment_required')) {
            setError('Verify your identity with an authenticator app first (sign in to set one up), then try again.');
            return;
          }
          // Found live in the review-round browser smoke: EdgeFunctionError's
          // own `.message` is the machine CODE (e.g. "url_domain_not_allowed"),
          // not the host-readable text the server actually wrote.
          // `.friendlyMessage` prefers the body's own `message` field.
          if (err instanceof EdgeFunctionError) {
            setError(err.friendlyMessage);
            return;
          }
          setError(err instanceof Error ? err.message : 'Could not add this pay rail.');
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-md flex flex-col gap-sm rounded-input border border-hair p-md">
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        Provider
        <select
          value={provider}
          onChange={(e) => {
            const next = e.target.value as PayRailProvider;
            setProvider(next);
            if (!BANK_PROVIDERS.includes(next) && kind === 'bank_details') setKind('static_link');
          }}
          className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
        >
          {PROVIDER_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-xs text-caption text-ink-2">
        Kind
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as PayRailKind)}
          className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
        >
          <option value="static_link">Static pay link</option>
          <option value="param_link">Pay link with amount parameter</option>
          {BANK_PROVIDERS.includes(provider) ? <option value="bank_details">Bank details</option> : null}
        </select>
      </label>
      {isBank ? (
        <>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Account holder
            <input
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
              required
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Account number
            <input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              required
              inputMode="numeric"
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Branch code
            <input
              value={branchCode}
              onChange={(e) => setBranchCode(e.target.value)}
              required
              inputMode="numeric"
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
        </>
      ) : (
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          Pay link URL
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://…"
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
          />
        </label>
      )}
      {error ? <p className="text-body text-danger">{error}</p> : null}
      <button
        type="submit"
        disabled={createRail.isPending}
        className="self-start rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
      >
        {createRail.isPending ? 'Adding…' : 'Add pay rail'}
      </button>
    </form>
  );
}

function ListingSettlementForm({
  listingId,
  businessFallbackPct,
  businessFallbackTerms,
  fullPrepayUnlocked,
  policyLabel,
  legacy,
}: {
  listingId: string;
  businessFallbackPct: number | string | null;
  businessFallbackTerms: string | null;
  fullPrepayUnlocked: boolean;
  policyLabel: string;
  legacy: boolean;
}) {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const settlementQuery = useListingSettlement(supabase, listingId);
  const railsQuery = usePayRails(supabase, businessId);
  const writeSettlement = useWriteListingSettlement(supabase);

  const row = settlementQuery.data;
  const [mode, setMode] = useState<SettlementMode>(row?.mode ?? 'at_desk');
  const [depositPct, setDepositPct] = useState<string>(row?.deposit_pct != null ? String(row.deposit_pct) : '20');
  const [railId, setRailId] = useState<string>(row?.rail_id ?? '');
  const [refundTerms, setRefundTerms] = useState<string>(row?.refund_terms ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Sync local form state whenever the loaded row changes — the initial
  // async load, a listing switch, OR a refetch after this form's own
  // successful save (useWriteListingSettlement's onSuccess invalidates the
  // query so the fields reflect what actually landed).
  useEffect(() => {
    setMode(row?.mode ?? 'at_desk');
    setDepositPct(row?.deposit_pct != null ? String(row.deposit_pct) : '20');
    setRailId(row?.rail_id ?? '');
    setRefundTerms(row?.refund_terms ?? '');
  }, [row?.mode, row?.deposit_pct, row?.rail_id, row?.refund_terms]);

  // "Saved."/error resets on an actual listing switch only — NOT in the
  // effect above, which also fires from the save's own invalidated
  // refetch (BUG-1 unfixme, Task 29: that refetch landed a moment after
  // the mutation's onSuccess set `saved`, immediately clearing the
  // confirmation before it could ever be seen).
  useEffect(() => {
    setSaved(false);
    setError(null);
  }, [listingId]);

  const activeRails = (railsQuery.data ?? []).filter((r) => r.status === 'active');
  const capExceeded = mode === 'deposit_pct' && Number(depositPct) > 30 && !fullPrepayUnlocked;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const pct = mode === 'deposit_pct' ? Number(depositPct) : null;
    if (mode !== 'at_desk' && !railId) {
      setError('Pick an active pay rail for an advance mode, or set the mode to "at the desk".');
      return;
    }
    writeSettlement.mutate(
      {
        listingId,
        mode,
        depositPct: pct,
        railId: mode === 'at_desk' ? null : railId,
        refundTerms: refundTerms.trim() || null,
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setError(err instanceof Error ? err.message : 'Could not save.'),
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-sm">
      <fieldset disabled={legacy} className="flex flex-col gap-sm">
        <p className="text-caption text-ink-3">
          Fallback if unset: {businessFallbackPct ? `${businessFallbackPct}%` : 'pay at the desk'} (display only, no
          cap, no rail).
        </p>
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          Mode
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as SettlementMode)}
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
          >
            {SETTLEMENT_MODES.map((m) => (
              <option key={m} value={m}>
                {m === 'at_desk' ? 'Pay at the desk' : m === 'deposit_pct' ? 'Advance payment % (paid to you before arrival)' : 'Full prepay'}
              </option>
            ))}
          </select>
        </label>
        {mode === 'deposit_pct' ? (
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Advance payment % ({fullPrepayUnlocked ? 'unlocked, up to 100' : 'max 30 unless unlocked'})
            <input
              type="number"
              min={1}
              max={100}
              step="0.01"
              value={depositPct}
              onChange={(e) => setDepositPct(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
        ) : null}
        {capExceeded ? (
          <p className="flex items-center gap-xs text-body text-danger">
            <AlertIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Advance payments above 30% need NamibiaGo to unlock full prepay for your business first.
          </p>
        ) : null}
        {mode !== 'at_desk' ? (
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Pay rail
            <select
              value={railId}
              onChange={(e) => setRailId(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            >
              <option value="">— choose an active rail —</option>
              {activeRails.map((r) => (
                <option key={r.id} value={r.id}>
                  {PROVIDER_OPTIONS.find((p) => p.value === r.provider)?.label ?? r.provider}
                </option>
              ))}
            </select>
            {activeRails.length === 0 ? (
              <span className="text-caption text-warning">No active rail yet — add and verify one above.</span>
            ) : null}
          </label>
        ) : null}
        {mode !== 'at_desk' ? (
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Additional refund notes (optional)
            <span className="text-caption text-ink-3">
              Shown to the guest with your cancellation policy. The policy always decides refunds.
            </span>
            <span className="text-caption text-ink-3">Your cancellation policy: {policyLabel}.</span>
            <textarea
              value={refundTerms}
              onChange={(e) => setRefundTerms(e.target.value)}
              placeholder={businessFallbackTerms ?? 'e.g. Refunds go back to the card you paid with within 7 working days.'}
              rows={3}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
        ) : null}
        {error ? <p className="text-body text-danger">{error}</p> : null}
        {saved ? <p className="text-body text-success">Saved.</p> : null}
        <button
          type="submit"
          disabled={writeSettlement.isPending || capExceeded}
          className="self-start rounded-button bg-canyon px-lg py-sm text-body font-bold text-white disabled:opacity-50"
        >
          {writeSettlement.isPending ? 'Saving…' : 'Save settlement'}
        </button>
      </fieldset>
    </form>
  );
}
